import type { ContatoInterno } from '@mymaxx/shared';
import { Aviso, Botao } from '@mymaxx/ui';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Check, Search, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ErroApi } from '../lib/api';
import { internoApi } from '../lib/interno-api';
import { useSessao } from '../lib/sessao';

/**
 * Escolher com quem começar uma conversa, no celular.
 *
 * Tela inteira, e não uma janela flutuante como no painel: numa tela de 375px um modal com
 * busca e lista rolável vira uma caixinha dentro de outra, e o teclado cobre metade dela.
 *
 * Um toque num nome abre a conversa direto — é o que se quer em nove de cada dez vezes.
 * Para montar grupo, a pessoa marca o segundo nome e a tela se transforma: aparece o campo
 * do nome do grupo e o botão muda. Ninguém precisa decidir "é conversa ou grupo?" antes de
 * saber com quem vai falar.
 */
export function NovaConversa({
  onFechar,
  onAberta,
}: {
  onFechar: () => void;
  onAberta: (canalId: string) => void;
}) {
  const [procurado, setProcurado] = useState('');
  /** O termo que de fato vai ao servidor — segurado para não buscar a cada tecla. */
  const [termo, setTermo] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setTermo(procurado.trim()), 300);
    return () => clearTimeout(id);
  }, [procurado]);

  const [escolhidos, setEscolhidos] = useState<ContatoInterno[]>([]);
  const [nomeGrupo, setNomeGrupo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const meuId = useSessao((e) => e.cliente?.id);
  const contatos = useQuery({
    queryKey: ['interno-contatos', termo],
    queryFn: () => internoApi.contatos(termo || undefined),
  });

  // Eu fora da lista: o servidor recusa conversa consigo mesmo, e oferecer uma opção que
  // só devolve erro é pior do que não oferecer.
  const lista = (contatos.data ?? []).filter((c) => !(c.tipo === 'tecnico' && c.id === meuId));

  const virouGrupo = escolhidos.length > 1;
  const marcado = (c: ContatoInterno) => escolhidos.some((e) => e.tipo === c.tipo && e.id === c.id);

  const alternar = (c: ContatoInterno) =>
    setEscolhidos((atual) =>
      atual.some((e) => e.tipo === c.tipo && e.id === c.id)
        ? atual.filter((e) => !(e.tipo === c.tipo && e.id === c.id))
        : [...atual, c],
    );

  async function abrir(so?: ContatoInterno) {
    const lista = so ? [so] : escolhidos;
    if (lista.length === 0) return;
    setErro(null);
    setOcupado(true);
    try {
      if (lista.length === 1) {
        const um = lista[0]!;
        const canal = await internoApi.abrirDireto(
          um.tipo === 'usuario' ? { usuarioId: um.id } : { clienteId: um.id },
        );
        onAberta(canal.id);
        return;
      }
      const canal = await internoApi.criarGrupo({
        nome: nomeGrupo.trim() || 'Grupo',
        descricao: '',
        usuarioIds: lista.filter((e) => e.tipo === 'usuario').map((e) => e.id),
        clienteIds: lista.filter((e) => e.tipo === 'tecnico').map((e) => e.id),
      });
      onAberta(canal.id);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Não consegui abrir a conversa');
    } finally {
      setOcupado(false);
    }
  }

  return (
    <main className="flex min-h-dvh flex-col bg-neutral-50 dark:bg-neutral-950">
      <header className="flex items-center gap-3 border-b border-neutral-200 bg-white px-4 py-3 dark:border-neutral-800 dark:bg-neutral-900">
        <button type="button" onClick={onFechar} aria-label="Voltar" className="text-neutral-500">
          <ArrowLeft size={20} />
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">
            {virouGrupo ? `Novo grupo · ${escolhidos.length} pessoas` : 'Nova conversa'}
          </p>
          <p className="truncate text-xs text-neutral-500">
            {virouGrupo ? 'Dê um nome ao grupo abaixo' : 'Toque num nome para começar'}
          </p>
        </div>
      </header>

      <div className="border-b border-neutral-200 bg-white px-4 py-2 dark:border-neutral-800 dark:bg-neutral-900">
        <label className="flex items-center gap-2 rounded-lg bg-neutral-100 px-3 py-2 dark:bg-neutral-800">
          <Search size={16} className="shrink-0 text-neutral-400" />
          <input
            value={procurado}
            onChange={(e) => setProcurado(e.target.value)}
            placeholder="Procurar pessoa"
            aria-label="Procurar pessoa"
            className="min-w-0 flex-1 bg-transparent text-sm outline-none"
          />
        </label>
      </div>

      {virouGrupo && (
        <div className="border-b border-neutral-200 bg-white px-4 py-2 dark:border-neutral-800 dark:bg-neutral-900">
          <input
            value={nomeGrupo}
            onChange={(e) => setNomeGrupo(e.target.value)}
            placeholder="Nome do grupo"
            aria-label="Nome do grupo"
            className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-950"
          />
        </div>
      )}

      {erro && (
        <div className="p-3">
          <Aviso tipo="erro">{erro}</Aviso>
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        {contatos.isPending && (
          <p className="p-6 text-center text-sm text-neutral-500">Carregando…</p>
        )}
        {contatos.isSuccess && lista.length === 0 && (
          <p className="px-6 py-10 text-center text-sm text-neutral-500">
            {termo ? `Ninguém com "${termo}".` : 'Nenhum contato disponível.'}
          </p>
        )}
        <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
          {lista.map((c) => (
            <li key={`${c.tipo}:${c.id}`}>
              <button
                type="button"
                disabled={ocupado}
                onClick={() => (escolhidos.length > 0 ? alternar(c) : void abrir(c))}
                // Segurar marca para grupo sem precisar entender a regra do toque simples.
                onContextMenu={(e) => {
                  e.preventDefault();
                  alternar(c);
                }}
                className="flex w-full items-center gap-3 bg-white px-4 py-3 text-left disabled:opacity-60 dark:bg-neutral-900"
              >
                <span
                  className={`flex size-9 shrink-0 items-center justify-center rounded-full text-xs font-medium ${
                    marcado(c)
                      ? 'bg-marca-600 text-white'
                      : 'bg-neutral-200 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-200'
                  }`}
                >
                  {marcado(c) ? <Check size={16} /> : iniciais(c.nome)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{c.nome}</span>
                  <span className="block truncate text-xs text-neutral-500">
                    {c.descricao ?? (c.tipo === 'tecnico' ? 'Técnico' : 'Equipe')}
                  </span>
                </span>
                {c.online && <span className="size-2 shrink-0 rounded-full bg-emerald-500" />}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {escolhidos.length > 0 && (
        <div className="border-t border-neutral-200 bg-white p-3 dark:border-neutral-800 dark:bg-neutral-900">
          <Botao
            className="w-full"
            carregando={ocupado}
            disabled={virouGrupo && !nomeGrupo.trim()}
            onClick={() => void abrir()}
          >
            {virouGrupo ? (
              <>
                <Users size={15} /> Criar grupo
              </>
            ) : (
              'Conversar'
            )}
          </Botao>
        </div>
      )}
    </main>
  );
}

/** As duas primeiras letras do nome, para o círculo de quem não tem foto. */
function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/);
  return ((partes[0]?.[0] ?? '') + (partes[1]?.[0] ?? '')).toUpperCase() || '?';
}
